import { useQuery } from '@tanstack/react-query';
import { FC, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { _getMemberActivity, _getTeamActivity, _getTeamGoals } from '@/api/gamma-team/queries';
import { activityPeriod } from '@/api/gamma-team/types';
import { useGetUserProfile } from '@/api/user-dashboard/queries';
import { Button, DatePicker, type DateRange, theme, Typography } from '@/design-system';
import { Permission, usePermissions } from '@/shared/modules/permissions';

import { ActivityChart } from './activity-chart';
import { ActivityHeatmap, HEATMAP_MAX_ROWS } from './activity-heatmap';
import { DayDrawer } from './day-drawer';
import { MemberActivity } from './member-activity';
import { MemberComparison } from './member-comparison';
import { MemberDrawer } from './member-drawer';
import {
  ACTIVITY_DAY_FORMAT,
  DEFAULT_PERIOD,
  resolveTimeZone,
  toActivityDateRange,
  toActivityParams,
} from './period';
import { QuietLine, Section, SectionHeader, TabRoot, TwoColumns } from './styled';
import { TeamActivityFilters } from './team-activity-filters';
import { TeamKpis } from './team-kpis';
import { DEFAULT_TEAM_SORT } from './team-sorting';
import { listTeamMembers, TeamTable } from './team-table';
import {
  type Granularity,
  granularity as granularityValues,
  type MemberView,
  memberView,
  previousAverage,
  toBuckets,
} from './team-trends';
import { useTeamActivityFilters } from './use-team-activity-filters';

export const INCOMPLETE_RANGE_HINT = 'Pick a start and an end date to run the report.';
export const MEMBER_PARAM = 'member';
export const DAY_PARAM = 'day';

type DrawerTarget = { member: string } | { day: string } | null;

const useDrawerTarget = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const memberId = searchParams.get(MEMBER_PARAM);
  const day = memberId ? null : searchParams.get(DAY_PARAM);
  const open = useCallback(
    (target: DrawerTarget) =>
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          params.delete(MEMBER_PARAM);
          params.delete(DAY_PARAM);
          if (target && 'member' in target) params.set(MEMBER_PARAM, target.member);
          if (target && 'day' in target) params.set(DAY_PARAM, target.day);
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  return { memberId, day, open };
};

export const TeamActivityTab: FC = () => {
  const { has } = usePermissions();
  const canViewTeam = has(Permission.AdminTeamActivityView);
  const canEditGoals = has(Permission.AdminTeamActivityManage);
  const { user } = useGetUserProfile();
  const ownUserId = user?.id ?? null;
  const timeZone = useMemo(resolveTimeZone, []);
  const now = useMemo(() => new Date(), []);

  const filters = useTeamActivityFilters({ canViewTeam, ownUserId });
  const { period, from, to, members } = filters;
  const params = useMemo(
    () => toActivityParams({ period, from, to }, timeZone),
    [period, from, to, timeZone],
  );
  const requestParams = params ?? { period: DEFAULT_PERIOD, timeZone };

  const [granularity, setGranularity] = useState<Granularity>(granularityValues.Day);
  const [sort, setSort] = useState(DEFAULT_TEAM_SORT);
  const [view, setView] = useState<MemberView>(memberView.Active);
  const { memberId: drawerMemberId, day: drawerDay, open: openDrawer } = useDrawerTarget();

  const memberId = members?.length === 1 ? members[0] : null;
  const selectionIds = useMemo(
    () => (members && members.length > 0 ? [...members].sort() : null),
    [members],
  );
  const isMultiSelection = selectionIds !== null && selectionIds.length > 1;

  const teamQuery = useQuery({
    ..._getTeamActivity(requestParams),
    enabled: canViewTeam && params !== null,
  });

  const selectionQuery = useQuery({
    ..._getTeamActivity(
      selectionIds ? { ...requestParams, memberIds: selectionIds } : requestParams,
    ),
    enabled: canViewTeam && selectionIds !== null && params !== null,
  });

  const memberQuery = useQuery({
    ..._getMemberActivity(memberId ?? '', requestParams),
    enabled: memberId !== null && params !== null,
  });

  const goalsQuery = useQuery({ ..._getTeamGoals(), enabled: canViewTeam });

  const tableQuery = isMultiSelection ? selectionQuery : teamQuery;
  const trendQuery = selectionIds ? selectionQuery : teamQuery;
  const trendData = trendQuery.data;
  const range = memberId ? memberQuery.data?.range : tableQuery.data?.range;
  const currentMember = memberQuery.data?.member ?? null;
  const today = useMemo(() => new Date(), []);
  const shownRange = useMemo((): DateRange | null => {
    if (period === activityPeriod.Custom) return { from, to };
    return range ? toActivityDateRange(range) : null;
  }, [period, from, to, range]);

  const buckets = useMemo(
    () => (trendData ? toBuckets(trendData.daily ?? [], granularity) : null),
    [trendData, granularity],
  );
  const dayBounds = useMemo(() => {
    const daily = trendData?.daily;
    return daily?.length ? { first: daily[0].date, last: daily[daily.length - 1].date } : null;
  }, [trendData]);

  const tableMembers = useMemo(() => tableQuery.data?.members ?? [], [tableQuery.data]);
  const drawerOrder = useMemo(
    () =>
      listTeamMembers(tableMembers, sort, view, isMultiSelection, now).map(({ userId }) => userId),
    [tableMembers, sort, view, isMultiSelection, now],
  );
  const drawerMember = useMemo(
    () =>
      drawerMemberId
        ? (tableMembers.find((member) => member.userId === drawerMemberId) ??
          teamQuery.data?.members.find((member) => member.userId === drawerMemberId) ??
          null)
        : null,
    [drawerMemberId, tableMembers, teamQuery.data],
  );

  const handleShownRangeChange = (next: DateRange) => {
    if (next.from || next.to) {
      filters.setRange(next);
    } else {
      filters.setPeriod(DEFAULT_PERIOD);
    }
  };

  const { setMembers } = filters;
  const openMember = useCallback((userId: string) => openDrawer({ member: userId }), [openDrawer]);
  const openDay = useCallback((day: string) => openDrawer({ day }), [openDrawer]);
  const closeDrawer = useCallback(() => openDrawer(null), [openDrawer]);

  const heatmapRows = useMemo(() => {
    if (!tableMembers.some((member) => member.dailyActivity)) return null;
    const active = tableMembers.filter((member) =>
      (member.dailyActivity ?? []).some((value) => value > 0),
    ).length;
    return Math.min(HEATMAP_MAX_ROWS, active);
  }, [tableMembers]);

  const teamFailed = tableQuery.isError && !tableQuery.data;
  const trendFailed = canViewTeam && trendQuery.isError && !trendData;
  const showTeamCharts = canViewTeam && !trendFailed;
  const showTrendError = trendFailed && (memberId !== null || !teamFailed);

  return (
    <TabRoot>
      <Section>
        <SectionHeader>
          <Typography variant="h5">Team activity</Typography>
          {shownRange && (
            <DatePicker
              months="range"
              placeholder="Select dates"
              dateFormat={ACTIVITY_DAY_FORMAT}
              maxDate={today}
              value={shownRange}
              onChange={handleShownRangeChange}
            />
          )}
        </SectionHeader>

        <TeamActivityFilters
          period={filters.period}
          from={filters.from}
          to={filters.to}
          members={members}
          canViewTeam={canViewTeam}
          options={teamQuery.data?.members ?? []}
          currentMember={currentMember}
          onPeriodChange={filters.setPeriod}
          onRangeChange={filters.setRange}
          onMembersChange={setMembers}
        />
      </Section>

      {params === null ? (
        <QuietLine role="status">
          <Typography variant="body2" color={theme.colors.text.secondary}>
            {INCOMPLETE_RANGE_HINT}
          </Typography>
        </QuietLine>
      ) : (
        <>
          {showTrendError && (
            <QuietLine role="alert">
              <Typography variant="body2" color={theme.colors.text.secondary}>
                Activity charts couldn’t be loaded.
              </Typography>
              <Button
                variant="outline"
                color="tertiary"
                size="md"
                disabled={trendQuery.isFetching}
                onClick={() => trendQuery.refetch()}
              >
                {trendQuery.isFetching ? 'Loading…' : 'Try again'}
              </Button>
            </QuietLine>
          )}
          {showTeamCharts && (
            <>
              <TeamKpis data={trendData} />
              <ActivityChart
                buckets={buckets}
                granularity={granularity}
                onGranularityChange={setGranularity}
                previousAverage={
                  trendData ? previousAverage(trendData, 'entries', granularity) : null
                }
                onSelectDay={openDay}
              />
              {!memberId && (
                <TwoColumns>
                  <MemberComparison
                    members={tableQuery.data?.members ?? null}
                    rowLimit={heatmapRows ?? undefined}
                    onSelectMember={openMember}
                  />
                  <ActivityHeatmap
                    daily={trendData?.daily ?? null}
                    members={tableQuery.data?.members ?? null}
                    granularity={granularity}
                    onSelectMember={openMember}
                  />
                </TwoColumns>
              )}
            </>
          )}

          {memberId ? (
            <MemberActivity
              key={memberId}
              data={memberQuery.data}
              isPending={memberQuery.isPending}
              isError={memberQuery.isError}
              isFetching={memberQuery.isFetching}
              onRetry={() => memberQuery.refetch()}
            />
          ) : teamFailed ? (
            <QuietLine role="alert">
              <Typography variant="body2" color={theme.colors.text.secondary}>
                Team activity couldn’t be loaded.
              </Typography>
              <Button
                variant="outline"
                color="tertiary"
                size="md"
                disabled={tableQuery.isFetching}
                onClick={() => tableQuery.refetch()}
              >
                {tableQuery.isFetching ? 'Loading…' : 'Try again'}
              </Button>
            </QuietLine>
          ) : (
            <TeamTable
              members={tableMembers}
              total={tableQuery.data?.total ?? null}
              range={tableQuery.data?.range}
              isLoading={members === null || tableQuery.isPending}
              isSelection={isMultiSelection}
              sort={sort}
              onSortChange={setSort}
              view={view}
              onViewChange={setView}
              selectedMemberId={drawerMemberId}
              now={now}
              onSelectMember={openMember}
            />
          )}
        </>
      )}

      {canViewTeam && (
        <>
          <MemberDrawer
            member={drawerMember}
            order={drawerOrder}
            daily={tableQuery.data?.daily}
            params={requestParams}
            goals={goalsQuery.data?.goals}
            goalsFailed={goalsQuery.isError}
            onRetryGoals={() => void goalsQuery.refetch()}
            canEditGoals={canEditGoals}
            onChangeMember={openMember}
            onClose={closeDrawer}
          />
          <DayDrawer
            day={drawerDay}
            timeZone={timeZone}
            memberIds={selectionIds ?? undefined}
            bounds={dayBounds}
            onChangeDay={openDay}
            onOpenMember={openMember}
            onClose={closeDrawer}
          />
        </>
      )}
    </TabRoot>
  );
};

export default TeamActivityTab;
